import React, { useEffect, useMemo, useState } from 'react';
import cls from 'classnames';

import { MerchantAttribution } from 'src/shoppe/elements/MerchantAttribution';
import { IMerchant, IMerchantReview } from 'src/explore/types/shoppe';

import { Review } from './molecules/Review';

import styles from './index.module.sass';

interface IReview extends IMerchantReview {}

interface IProps {
  className?: string;
  maxLength?: number;
  merchant?: IMerchant;
  reviews: IReview[];
  scopedReviews?: IReview[];
  scopedCount?: number;
  totalCount?: number;
  onScoped?: ( state: boolean ) => void;
}

export const Reviews = ({
  className,
  maxLength,
  merchant,
  reviews,
  scopedReviews,
  scopedCount,
  totalCount,
  onScoped,
}: IProps ) => {
  const hasScopedReviews = useMemo(
    () => scopedReviews && scopedReviews.length > 0,
    [ scopedReviews ]
  );

  const [ reviewsForDisplay, setReviewsForDisplay ] = useState<IReview[]>(
    hasScopedReviews ? scopedReviews : reviews
  );
  const [ showingScopedReviews, setShowingScopedReviews ] = useState<boolean>( hasScopedReviews );

  useEffect(() => {
    setReviewsForDisplay( showingScopedReviews ? scopedReviews : reviews );
  }, [ reviews, scopedReviews, showingScopedReviews ]);

  if ( reviewsForDisplay?.length === 0 ) return null;

  const visibleReviews = maxLength ? reviewsForDisplay.slice( 0, maxLength ) : reviewsForDisplay;

  const handleReviewToggle = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    scoped: boolean
  ) => {
    event.preventDefault();
    setReviewsForDisplay( scoped ? scopedReviews : reviews );
    setShowingScopedReviews( scoped );

    onScoped?.( scoped );
  };

  return (
    <section id="merchant-reviews" className={cls( 'spec__reviews', className )}>
      <header className="row justify-content-center">
        <div className="col">
          <div className="border-bottom pb-2">
            <div className="d-flex align-items-center">
              <h3 className="fs-8">Customer Reviews</h3>
              <div className={`${styles.about} ml-3 mt-2 fs-4`}>
                {'Our reviews are 100% verified customer purchases. To learn more, visit our '}
                <a
                  href="https://help.goldbelly.com/hc/en-us/articles/17646872406043"
                  target="_blank"
                  rel="noreferrer"
                >
                  FAQ page
                </a>
                .
              </div>
            </div>

            {merchant && merchant.loveScore && (
              <MerchantAttribution
                attribution="For"
                className="mt-2 mb-1 fs-5"
                hideStarShipper
                merchant={merchant}
              />
            )}
          </div>

          <div className="mt-6 fs-5 d-flex align-items-center overflow-auto">
            {hasScopedReviews && (
              <>
                <button
                  type="button"
                  className={cls(
                    styles.reviewBtn,
                    'btn btn-link pl-0 fs-5 font-weight-bold spec__show-scoped-reviews',
                    showingScopedReviews ? styles.active : 'text-muted'
                  )}
                  onClick={( e ) => handleReviewToggle( e, true )}
                >
                  {`For this Product (${scopedCount || scopedReviews.length})`}
                </button>

                {' | '}
              </>
            )}

            <button
              type="button"
              className={cls(
                styles.reviewBtn,
                'btn btn-link fs-5 font-weight-bold spec__show-unscoped-reviews',
                showingScopedReviews ? 'text-muted' : styles.active
              )}
              onClick={( e ) => handleReviewToggle( e, false )}
            >
              {`For this Food Maker (${totalCount || reviews.length})`}
            </button>
          </div>
        </div>
      </header>

      <div className={`${styles.reviews} position-relative mt-6`}>
        <div className="row">
          {visibleReviews.map(( review ) => (
            <div className={`${styles.review} col-12 col-md-6 fs-4`} key={review.id}>
              <Review
                review={{
                  authorLocation: review.location,
                  authorName: review.customerName,
                  content: review.bodyHtml,
                  createdOn: review.createdAt,
                }}
                showDate
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
